import {Injectable} from '@angular/core';
import {Action, createSelector, State, StateContext} from '@ngxs/store';
import {patch} from '@ngxs/store/operators';
import {JsonApiResponse} from '@tisa-group/cdk';
import {OttEventService} from '@tisa-group/core/api/umpire';
import {UmpireClub, UmpireOttEvent, UmpireTag} from '@tisa-group/models';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {LoadLinkedEvents} from './linked-events.actions';

export type LinkedEventsStateModel = Record<string, JsonApiResponse<UmpireOttEvent[]>>;

@State<LinkedEventsStateModel>({
  name: 'LinkedEvent',
  defaults: {},
})
@Injectable()
export class LinkedEventsState {
  @Action(LoadLinkedEvents)
  LoadLinkedEvents(
    ctx: StateContext<LinkedEventsStateModel>,
    action: LoadLinkedEvents,
  ): Observable<JsonApiResponse<UmpireOttEvent[]>> | JsonApiResponse<UmpireOttEvent[]> {
    let normalizedTags = '';

    if (action.tags) {
      normalizedTags = LinkedEventsState.normalizeTags(action.tags);
    }

    if (action.club) {
      normalizedTags = LinkedEventsState.normalizeClub(action.club);
    }
    const stateEvent = ctx.getState()[normalizedTags];

    if (stateEvent) {
      return stateEvent;
    }

    return this.eventService
      .list({
        include: ['image_horizontal', 'format', 'tags'].join(','),
        filter: {tags_name: {rule: 'in', value: normalizedTags}},
        sort: ['custom_events_sort'],
        page: {
          size: 18,
          number: 0,
        },
      })
      .pipe(
        tap(res =>
          ctx.setState(
            patch({
              [normalizedTags]: res,
            }),
          ),
        ),
      );
  }

  static getLinkedEventsByEvent(
    event: UmpireOttEvent,
  ): (state: LinkedEventsStateModel) => UmpireOttEvent[] {
    return createSelector<(state: LinkedEventsStateModel) => UmpireOttEvent[]>(
      [LinkedEventsState],
      state => {
        return state[LinkedEventsState.normalizeTags(event.tags || [])]?.data.filter(
          linkedEvent => event.id !== linkedEvent.id,
        );
      },
    );
  }

  static getLinkedEventsByClub(
    club: UmpireClub,
  ): (state: LinkedEventsStateModel) => UmpireOttEvent[] {
    return createSelector<(state: LinkedEventsStateModel) => UmpireOttEvent[]>(
      [LinkedEventsState],
      state => {
        return state[LinkedEventsState.normalizeClub(club)]?.data;
      },
    );
  }

  static normalizeTags = (tags: UmpireTag[]) =>
    [...new Set(tags.map(tag => tag.name))].sort().join(',');

  static normalizeClub = (club: UmpireClub) =>
    [
      club.name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ł/g, 'l')
        .replace(/Ł/g, 'L'),
      club.name,
    ].join(',');

  constructor(readonly eventService: OttEventService) {}
}
